import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { backToDashboardStr } from '@piqet-angular/shared';

export interface ErrorPageConfig {
  errorCode?: string;
  errorTitle: string;
  errorMessage: string;
  buttonText?: string;
  buttonRoute?: string;
  showErrorCode?: boolean;
}

@Component({
  selector: 'piqet-angular-not-found',
  templateUrl: './not-found.component.html',
  styleUrls: ['./not-found.component.scss'],
})
export class NotFoundComponent implements OnInit {
  @Input() config?: ErrorPageConfig;

  backToDashboardStr = backToDashboardStr;
  isUnAuthorized = false;
  isForbidden = false;
  isInValidParams = false;

  errorCode = '404';
  errorTitle = 'Page not found';
  errorMessage = 'The page you are looking for is no longer available';
  buttonText = this.backToDashboardStr;
  buttonRoute = '/dashboard';
  showErrorCode = true;

  notFoundStr = 'The page you are looking for is no longer available';
  unAuthorizedStr = 'You do not have the permissions to view this page';
  forbiddenStr = 'Access denied for this operation';
  inValidParams = 'The page you are trying to access is invalid or no longer available';

  constructor(private readonly route: ActivatedRoute, private readonly router: Router) {}

  ngOnInit(): void {
    const navigation = this.router.getCurrentNavigation();
    const stateConfig = navigation?.extras?.state?.['config'] as ErrorPageConfig;
    const historyStateConfig = history.state?.config as ErrorPageConfig;

    if (this.config) {
      this.applyConfig(this.config);
    } else if (stateConfig) {
      this.applyConfig(stateConfig);
    } else if (historyStateConfig) {
      this.applyConfig(historyStateConfig);
    } else {
      this.setConfigFromRoute();
    }
  }

  private applyConfig(config: ErrorPageConfig): void {
    this.errorCode = config.errorCode ?? '';
    this.errorTitle = config.errorTitle;
    this.errorMessage = config.errorMessage;
    this.buttonText = config.buttonText ?? this.backToDashboardStr;
    this.buttonRoute = config.buttonRoute ?? '/dashboard';
    this.showErrorCode = config.showErrorCode !== false;

    this.isUnAuthorized = this.errorCode === '401';
    this.isForbidden = this.errorCode === '403';
    this.isInValidParams = !this.errorCode;
  }

  private setConfigFromRoute(): void {
    const currentRoute = this.route.snapshot.routeConfig?.path;

    this.isUnAuthorized = currentRoute === 'unauthorized';
    this.isForbidden = currentRoute === 'forbidden';
    this.isInValidParams = currentRoute === 'invalidparams';

    if (this.isUnAuthorized) {
      this.errorCode = '401';
      this.errorTitle = 'Unauthorized access';
      this.errorMessage = this.unAuthorizedStr;
      this.buttonText = 'Redirect to login';
      this.buttonRoute = '/login';
    } else if (this.isForbidden) {
      this.errorCode = '403';
      this.errorTitle = 'Forbidden';
      this.errorMessage = this.forbiddenStr;
    } else if (this.isInValidParams) {
      this.errorCode = '';
      this.errorTitle = 'Invalid URL';
      this.errorMessage = this.inValidParams;
      this.showErrorCode = false;
    } else {
      this.errorCode = '404';
      this.errorTitle = 'Page not found';
      this.errorMessage = this.notFoundStr;
    }
  }
}
